/*
 * ====================================================================
 * STRATAKEY — GA4 Tracking Plan
 * ====================================================================
 * Measurement ID: G-XXXXXXXXXX
 * → Replace G-XXXXXXXXXX in /index.html (two occurrences)
 * → Get your ID: analytics.google.com → Admin → Data Streams → Web
 *
 * EVENT CATALOGUE
 * ┌──────────────────────────┬──────────────────────────────┬──────────────────────────────────────────┐
 * │ Event                    │ Parameters                   │ Trigger                                  │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ cta_click                │ button: hero_cta             │ Primary "Start Free Trial" CTA clicked   │
 * │                          │       | pricing_cta          │                                          │
 * │                          │       | footer_cta           │                                          │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ pricing_view             │ —                            │ Pricing section scrolls into viewport    │
 * │                          │                              │ (IntersectionObserver, fires once)       │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ plan_selected            │ plan_name: host              │ Specific plan CTA button clicked         │
 * │                          │          | individual        │                                          │
 * │                          │          | strata_council    │                                          │
 * │                          │          | property_manager  │                                          │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ building_count_changed   │ count: number                │ Property Manager +/− stepper changes     │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ faq_opened               │ question: string             │ FAQ accordion item is expanded           │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ page_scroll_depth        │ depth: 25 | 50 | 75 | 100   │ Scroll threshold crossed (once each)     │
 * ├──────────────────────────┼──────────────────────────────┼──────────────────────────────────────────┤
 * │ outbound_link_click      │ url: string                  │ External link clicked                    │
 * └──────────────────────────┴──────────────────────────────┴──────────────────────────────────────────┘
 *
 * MARK AS CONVERSIONS in GA4 Admin → Events → Mark as conversion:
 *   plan_selected, cta_click
 *
 * CONSENT MODEL
 *   GA4 loads with analytics_storage: "denied" by default (see /index.html).
 *   Call grantGA4Consent() when the user accepts the cookie banner.
 *   Status is persisted in localStorage under "cookie_consent".
 * ====================================================================
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function fireEvent(name: string, params?: Record<string, unknown>) {
  if (typeof window?.gtag !== "function") return;
  window.gtag("event", name, params);
  if (import.meta.env.DEV) {
    console.info(`[GA4] ${name}`, params ?? "");
  }
}

// ─── Consent ──────────────────────────────────────────────────────────────────

export function grantGA4Consent() {
  if (typeof window?.gtag !== "function") return;
  window.gtag("consent", "update", { analytics_storage: "granted" });
  try { localStorage.setItem("cookie_consent", "accepted"); } catch { /* SSR guard */ }
}

export function denyGA4Consent() {
  if (typeof window?.gtag !== "function") return;
  window.gtag("consent", "update", { analytics_storage: "denied" });
  try { localStorage.setItem("cookie_consent", "declined"); } catch { /* SSR guard */ }
}

export function getConsentStatus(): "accepted" | "declined" | null {
  try {
    const v = localStorage.getItem("cookie_consent");
    if (v === "accepted" || v === "declined") return v;
  } catch { /* SSR guard */ }
  return null;
}

// ─── Typed event helpers ───────────────────────────────────────────────────────

export type CtaButton = "hero_cta" | "pricing_cta" | "footer_cta";
export type PlanName  = "host" | "individual" | "strata_council" | "property_manager";

export function trackCtaClick(button: CtaButton) {
  fireEvent("cta_click", { button });
}

export function trackPricingView() {
  fireEvent("pricing_view");
}

export function trackPlanSelected(plan_name: PlanName) {
  fireEvent("plan_selected", { plan_name });
}

export function trackBuildingCountChanged(count: number) {
  fireEvent("building_count_changed", { count });
}

export function trackFaqOpened(question: string) {
  fireEvent("faq_opened", { question });
}

export function trackScrollDepth(depth: 25 | 50 | 75 | 100) {
  fireEvent("page_scroll_depth", { depth });
}

export function trackOutboundLink(url: string) {
  fireEvent("outbound_link_click", { url });
}

// ─── Scroll depth tracker — attach once via useEffect ─────────────────────────

export function initScrollDepthTracking(): () => void {
  if (typeof window === "undefined") return () => {};
  const reached = new Set<number>();
  const thresholds = [25, 50, 75, 100] as const;

  const check = () => {
    const scrolled = window.scrollY + window.innerHeight;
    const total    = document.documentElement.scrollHeight;
    const pct      = Math.round((scrolled / total) * 100);
    for (const t of thresholds) {
      if (pct >= t && !reached.has(t)) {
        reached.add(t);
        trackScrollDepth(t);
      }
    }
  };

  window.addEventListener("scroll", check, { passive: true });
  return () => window.removeEventListener("scroll", check);
}

// ─── Outbound link tracker — attach once via useEffect ────────────────────────

export function initOutboundLinkTracking(): () => void {
  if (typeof window === "undefined") return () => {};

  const handler = (e: MouseEvent) => {
    const anchor = (e.target as Element).closest("a");
    if (!anchor?.href) return;
    try {
      const url = new URL(anchor.href);
      if (url.protocol !== "javascript:" && url.hostname !== window.location.hostname) {
        trackOutboundLink(anchor.href);
      }
    } catch { /* malformed URL — ignore */ }
  };

  document.addEventListener("click", handler);
  return () => document.removeEventListener("click", handler);
}
